import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type {
  ComplianceDocumentI18nNamespace,
  ComplianceDocumentTranslationKeyPrefix,
} from './complianceDocumentEditorTypes'

type RenameComplianceDocumentSectionSheetProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: (sectionTitle: string) => void
  initialTitle: string
  translationKeyPrefix: ComplianceDocumentTranslationKeyPrefix
  i18nNamespace: ComplianceDocumentI18nNamespace
  inputIdPrefix: string
}

export function RenameComplianceDocumentSectionSheet({
  isOpen,
  onClose,
  onConfirm,
  initialTitle,
  translationKeyPrefix,
  i18nNamespace,
  inputIdPrefix,
}: RenameComplianceDocumentSectionSheetProps) {
  const { t } = useTranslation(i18nNamespace)
  const [sectionName, setSectionName] = useState(initialTitle)
  const [showNameRequired, setShowNameRequired] = useState(false)
  const sectionNameInputId = `${inputIdPrefix}-rename-section-name`

  useEffect(() => {
    if (!isOpen) {
      setShowNameRequired(false)
      return
    }
    setSectionName(initialTitle)
  }, [initialTitle, isOpen])

  const handleConfirm = () => {
    const trimmedName = sectionName.trim()
    if (trimmedName.length === 0) {
      setShowNameRequired(true)
      return
    }
    onConfirm(trimmedName)
    onClose()
  }

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <SheetContent
        side='right'
        className='flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-md'
      >
        <div className='flex min-h-0 flex-1 flex-col'>
          <SheetHeader className='shrink-0 border-b px-6 py-4 text-start'>
            <SheetTitle>
              {t(`${translationKeyPrefix}.renameSectionSheet.title`)}
            </SheetTitle>
          </SheetHeader>
          <div className='flex-1 space-y-2 overflow-y-auto px-6 py-4'>
            <p className='text-sm text-muted-foreground'>
              {t(`${translationKeyPrefix}.renameSectionSheet.description`)}
            </p>
            <Label htmlFor={sectionNameInputId}>
              {t(`${translationKeyPrefix}.renameSectionSheet.nameLabel`)}
            </Label>
            <Input
              id={sectionNameInputId}
              value={sectionName}
              autoFocus
              placeholder={t(
                `${translationKeyPrefix}.renameSectionSheet.namePlaceholder`
              )}
              onChange={(event) => {
                setSectionName(event.target.value)
                if (showNameRequired && event.target.value.trim().length > 0) {
                  setShowNameRequired(false)
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleConfirm()
                }
              }}
            />
            {showNameRequired ? (
              <p className='text-sm text-destructive'>
                {t(`${translationKeyPrefix}.renameSectionSheet.nameRequired`)}
              </p>
            ) : null}
          </div>
          <SheetFooter className='shrink-0 border-t px-6 py-4 sm:flex-row sm:justify-end'>
            <Button type='button' variant='outline' onClick={onClose}>
              {t(`${translationKeyPrefix}.cancel`)}
            </Button>
            <Button type='button' onClick={handleConfirm}>
              {t(`${translationKeyPrefix}.renameSectionSheet.confirm`)}
            </Button>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  )
}
